import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BadgeCheck, Building2, ShieldOff, UserPlus, ClipboardList, Trash2, RotateCcw, AlertCircle, Landmark } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { api, formatError } from '../lib/api';
import { toast } from 'sonner';

const TABS = [
  { id: 'verify', label: 'Verify tradies', icon: <BadgeCheck size={14} /> },
  { id: 'claims', label: 'Claim requests', icon: <ClipboardList size={14} /> },
  { id: 'removals', label: 'Removal requests', icon: <ShieldOff size={14} /> },
  { id: 'nominations', label: 'Nominations', icon: <UserPlus size={14} /> },
  { id: 'enterprise', label: 'Enterprise inquiries', icon: <Landmark size={14} /> },
];

export default function AdminPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState('verify');
  const [dash, setDash] = useState(null);
  const [pending, setPending] = useState([]);
  const [claims, setClaims] = useState([]);
  const [removals, setRemovals] = useState([]);
  const [noms, setNoms] = useState([]);
  const [inqs, setInqs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, p, c, r, n, e] = await Promise.all([
        api.adminDashboard(),
        api.adminPending(),
        api.adminClaimRequests(),
        api.adminRemovalRequests(),
        api.adminNominations(),
        api.adminEnterpriseInquiries(),
      ]);
      setDash(d.data); setPending(p.data); setClaims(c.data);
      setRemovals(r.data); setNoms(n.data); setInqs(e.data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (user && user.role !== 'admin') return <Navigate to="/" replace />;

  const verify = async (id) => {
    try { await api.adminVerify(id); toast.success('Tradie verified'); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const resolveRemoval = async (id, action) => {
    try {
      await api.adminResolveRemoval(id, action);
      toast.success(action === 'delete' ? 'Listing deleted' : 'Listing restored');
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-page">
      <Header />
      <div className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D96C4E]">Admin console</div>
        <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A]">Operations</h1>

        {/* Stats bar */}
        {dash && (
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-stats">
            {[
              { n: dash.verified_tradies, label: 'Verified tradies', color: '#2A9D8F' },
              { n: dash.unclaimed_listings, label: 'Unclaimed listings', color: '#B08911' },
              { n: dash.pending_verifications + dash.claim_requests_pending, label: 'Awaiting your review', color: '#D96C4E' },
              { n: dash.agency_subs_trial, label: 'Agency trials active', color: '#1A362D' },
            ].map((s) => (
              <div key={s.label} className="border border-[#E5E4E0] bg-white p-4">
                <div className="font-heading text-3xl font-bold text-[#1C1B1A]">{s.n}</div>
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: s.color }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2 border-b border-[#E5E4E0]" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
                tab === t.id ? 'border-[#1A362D] text-[#1A362D]' : 'border-transparent text-[#8A8988] hover:text-[#1C1B1A]'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6" data-testid="admin-content">
          {loading && <div className="text-sm text-[#8A8988]">Loading...</div>}

          {tab === 'verify' && (
            <div className="space-y-3">
              {pending.length === 0 ? <Empty text="No tradies awaiting verification." /> : pending.map((t) => (
                <div key={t.id} className="border border-[#E5E4E0] bg-white p-6" data-testid={`pending-${t.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="font-heading text-xl font-bold text-[#1C1B1A]">{t.business_name}</div>
                      <div className="mt-1 text-sm text-[#5C5B5A]">{t.name} · {t.email} {t.phone && `· ${t.phone}`}</div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.15em] text-[#8A8988]">
                        {(t.categories || []).map((c) => <span key={c}>{c.replace('_', ' ')}</span>)}
                        <span>· {t.suburb}</span>
                      </div>
                      {t.bio && <p className="mt-3 max-w-2xl text-sm text-[#5C5B5A]">{t.bio}</p>}
                    </div>
                    <button onClick={() => verify(t.id)} data-testid={`verify-${t.id}`} className="btn-primary">
                      <BadgeCheck size={16} /> Verify
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'claims' && (
            <div className="space-y-3">
              {claims.length === 0 ? <Empty text="No pending claim requests." /> : claims.map((c) => (
                <div key={c.id} className="border border-[#E5E4E0] bg-white p-5" data-testid={`claim-${c.id}`}>
                  <div className="font-heading text-lg font-bold text-[#1C1B1A]">{c.business_name}</div>
                  <div className="mt-1 text-xs text-[#8A8988]">{new Date(c.created_at).toLocaleString()} · Status: {c.status}</div>
                  <div className="mt-2 text-xs text-[#5C5B5A]">Tradie ID: {c.tradie_id}</div>
                  <div className="mt-3 text-sm text-[#5C5B5A]">Verify their compliance docs in the Verify tab once they upload.</div>
                </div>
              ))}
            </div>
          )}

          {tab === 'removals' && (
            <div className="space-y-3">
              {removals.length === 0 ? <Empty text="No removal requests." /> : removals.map((r) => (
                <div key={r.id} className="border border-[#D96C4E]/40 bg-white p-5" data-testid={`removal-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <AlertCircle size={14} className="text-[#D96C4E]" />
                        <div className="font-heading text-lg font-bold text-[#1C1B1A]">{r.business_name}</div>
                      </div>
                      <div className="mt-1 text-xs text-[#8A8988]">{new Date(r.created_at).toLocaleString()} · Status: {r.status} · {r.contact_email}</div>
                      {r.reason && <p className="mt-3 text-sm text-[#5C5B5A]">"{r.reason}"</p>}
                    </div>
                    {r.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => resolveRemoval(r.id, 'delete')} data-testid={`remove-delete-${r.id}`} className="inline-flex items-center gap-1 bg-[#D96C4E] px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-white hover:bg-[#C25A3D]">
                          <Trash2 size={13} /> Delete
                        </button>
                        <button onClick={() => resolveRemoval(r.id, 'restore')} data-testid={`remove-restore-${r.id}`} className="inline-flex items-center gap-1 border border-[#E5E4E0] bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#5C5B5A] hover:border-[#1A362D]">
                          <RotateCcw size={13} /> Restore
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'nominations' && (
            <div className="space-y-3">
              {noms.length === 0 ? <Empty text="No tradie nominations yet." /> : noms.map((n) => (
                <div key={n.id} className="border border-[#E5E4E0] bg-white p-5" data-testid={`nom-${n.id}`}>
                  <div className="font-heading text-lg font-bold text-[#1C1B1A]">{n.business_name}</div>
                  <div className="mt-1 text-xs text-[#8A8988]">{new Date(n.created_at).toLocaleString()} · {n.category} · {n.suburb}</div>
                  <div className="mt-2 text-sm text-[#5C5B5A]">
                    Phone: {n.phone || '—'} · Website: {n.website || '—'}
                    <br />Referred by: {n.referrer_name || 'Anonymous'} {n.referrer_email && `<${n.referrer_email}>`}
                  </div>
                  {n.note && <p className="mt-2 text-sm text-[#5C5B5A]">"{n.note}"</p>}
                </div>
              ))}
            </div>
          )}

          {tab === 'enterprise' && (
            <div className="space-y-3">
              {inqs.length === 0 ? <Empty text="No enterprise inquiries yet." /> : inqs.map((e) => (
                <div key={e.id} className="border border-[#1A362D] bg-white p-5" data-testid={`inq-${e.id}`}>
                  <div className="flex items-center gap-2">
                    {e.org_type === 'government' || e.org_type === 'council' ? <Landmark size={14} className="text-[#D96C4E]" /> : <Building2 size={14} className="text-[#1A362D]" />}
                    <div className="font-heading text-lg font-bold text-[#1C1B1A]">{e.org_name}</div>
                    <span className="rounded-full border border-[#1A362D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#1A362D]">{e.org_type}</span>
                  </div>
                  <div className="mt-2 text-sm text-[#5C5B5A]">
                    {e.contact_name} · {e.email} {e.phone && `· ${e.phone}`}
                    <br />Plan interest: {e.plan_id || '—'} · Scope: {e.properties_or_scope || '—'}
                  </div>
                  {e.message && <p className="mt-2 text-sm text-[#5C5B5A]">"{e.message}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="border border-dashed border-[#E5E4E0] bg-white p-8 text-center text-sm text-[#8A8988]">{text}</div>;
}
